/**
 * `<caos-pill-container>` — a row of tags that keeps its own overflow and keyboard model.
 * (Catalog: Data display › PillContainer — a row of tags that keeps its own overflow and keyboard model.)
 *
 * CAOS-597. The library had the pill and not the container. Every row of tags was a bare flex box: a
 * record carrying forty labels drew forty, a keyboard user met each tag's dismiss control as a tab
 * stop of its own, and nothing said the tags belonged together. A multi-value field and the filter
 * chips on a list toolbar both need the container, so it is one piece.
 *
 * THE PILLS ARE TAGS PLACED INSIDE IT. `<caos-tag>` children are the pills, in the order they are
 * placed, each keeping everything a tag already does — its avatar, its error state, its removable
 * dismiss and the event that reports it. The container adds no pill of its own and never creates or
 * removes one.
 *
 * OVERFLOW. Given a `limit`, only that many pills show, followed by a "+N more" control; pressing it
 * shows the rest and becomes "Show fewer". Without a limit every pill shows and the row wraps.
 *
 * KEYBOARD. The whole row is one tab stop — the pill reached last, the first to begin with. Arrow keys
 * move between pills in reading direction (Left and Right swap under right-to-left), Up and Down
 * move backward and forward, and Home and End go to the ends. Delete or Backspace on a removable pill
 * asks for it to be removed: it raises the same `caos-tag-remove` the tag's own control raises, so
 * the host still does the removing, and once the pill has actually gone focus lands on the pill that
 * took its place, or on the one before it when it was the last. Because Delete is how the keyboard
 * removes a pill here, a tag placed in a container takes its own dismiss control out of the tab order
 * (see `tag.ts`) — otherwise a row of removable pills would be two stops, not one — and each removable
 * pill names the keys through `aria-keyshortcuts`.
 *
 * A11y: the row is a `role="list"` named by `label`, and each pill a `role="listitem"`. The "+N more"
 * control is a real button carrying `aria-expanded`. Styling seams: `::part(container)`,
 * `::part(more)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

const OVERFLOW = 'data-caos-overflow';

const sheet = makeSheet(`
  :host { display: block; }

  .container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--caos-space-1);
  }

  ::slotted([${OVERFLOW}]) { display: none; }

  .more {
    appearance: none;
    margin: 0;
    padding: 2px var(--caos-space-2);
    border: 0;
    border-radius: var(--caos-radius-sm);
    background: transparent;
    color: var(--caos-color-accent);
    font-family: var(--caos-font);
    font-size: 12px;
    line-height: 1.5;
    white-space: nowrap;
    cursor: pointer;
  }
  .more:hover { text-decoration: underline; }
  .more:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
`);

type Removable = HTMLElement & { requestRemove?: () => boolean };

export class CaosPillContainer extends CaosElement {
  static get observedAttributes(): string[] {
    return ['label', 'limit'];
  }

  private readonly container: HTMLDivElement;
  private readonly slotEl: HTMLSlotElement;
  private readonly moreButton: HTMLButtonElement;
  private showingAll = false;
  private activeIndex = 0;
  /** A pill that asked to be removed, and where it stood, until it has actually gone. */
  private removing: { pill: HTMLElement; index: number } | null = null;

  constructor() {
    super(sheet);
    this.container = document.createElement('div');
    this.container.className = 'container';
    this.container.setAttribute('part', 'container');
    this.container.setAttribute('role', 'list');

    this.slotEl = document.createElement('slot');
    // The pills are read from the element's own children, and re-read whenever they change, rather
    // than from the slot's assignment: a pill placed before the container is connected is already a
    // child, while the slot has not been assigned it yet.
    // `removable` is watched too, since a pill that becomes removable has to say Delete removes it.
    new MutationObserver(() => this.arrange()).observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['removable'],
    });

    this.moreButton = document.createElement('button');
    this.moreButton.type = 'button';
    this.moreButton.className = 'more';
    this.moreButton.setAttribute('part', 'more');
    this.moreButton.hidden = true;
    this.moreButton.addEventListener('click', () => {
      this.showingAll = !this.showingAll;
      this.arrange();
    });

    this.container.append(this.slotEl, this.moreButton);
    this.root.append(this.container);

    this.addEventListener('keydown', (e) => this.onKeydown(e));
    this.addEventListener('focusin', (e) => {
      const index = this.pills().indexOf(e.target as HTMLElement);
      if (index >= 0) {
        this.activeIndex = index;
        this.applyTabStops();
      }
    });
  }

  connectedCallback(): void {
    this.arrange();
  }

  attributeChangedCallback(): void {
    this.arrange();
  }

  /** How many pills show before the rest go behind "+N more". 0 — the default — shows them all. */
  get limit(): number {
    const n = Number(this.getAttribute('limit'));
    return Number.isInteger(n) && n > 0 ? n : 0;
  }
  set limit(v: number) {
    if (Number.isInteger(v) && v > 0) this.setAttribute('limit', String(v));
    else this.removeAttribute('limit');
  }

  /** Whether the pills past the limit are showing. */
  get expanded(): boolean {
    return this.showingAll;
  }

  /** Every element placed inside the default slot, in order. */
  private pills(): HTMLElement[] {
    return [...this.children].filter((el): el is HTMLElement => el instanceof HTMLElement && !el.slot);
  }

  private visiblePills(): HTMLElement[] {
    return this.pills().filter((pill) => !pill.hasAttribute(OVERFLOW));
  }

  private arrange(): void {
    const label = this.getAttribute('label');
    if (label) this.container.setAttribute('aria-label', label);
    else this.container.removeAttribute('aria-label');

    const pills = this.pills();
    const limit = this.limit;
    const overflowing = limit > 0 && pills.length > limit;
    if (!overflowing) this.showingAll = false;
    pills.forEach((pill, i) => {
      if (pill.getAttribute('role') !== 'listitem') pill.setAttribute('role', 'listitem');
      const keys = pill.hasAttribute('removable') ? 'Delete Backspace' : null;
      if (pill.getAttribute('aria-keyshortcuts') !== keys) {
        if (keys) pill.setAttribute('aria-keyshortcuts', keys);
        else pill.removeAttribute('aria-keyshortcuts');
      }
      const hide = overflowing && !this.showingAll && i >= limit;
      if (pill.hasAttribute(OVERFLOW) !== hide) pill.toggleAttribute(OVERFLOW, hide);
    });

    this.moreButton.hidden = !overflowing;
    const rest = pills.length - limit;
    this.moreButton.textContent = this.showingAll ? 'Show fewer' : `+${rest} more`;
    this.moreButton.setAttribute('aria-label', this.showingAll ? 'Show fewer' : `Show ${rest} more`);
    this.moreButton.setAttribute('aria-expanded', String(this.showingAll));

    const removing = this.removing;
    if (removing && !pills.includes(removing.pill)) {
      this.removing = null;
      const visible = this.visiblePills();
      const next = visible[Math.min(removing.index, visible.length - 1)];
      if (next) {
        this.activeIndex = pills.indexOf(next);
        this.applyTabStops();
        next.focus();
        return;
      }
    }
    this.applyTabStops();
  }

  /** One tab stop for the row: the pill reached last, kept on a pill that is showing. */
  private applyTabStops(): void {
    const pills = this.pills();
    const visible = this.visiblePills();
    if (visible.length === 0) return;
    const active = pills[this.activeIndex];
    const current = active && visible.includes(active) ? active : visible[Math.min(this.activeIndex, visible.length - 1)]!;
    this.activeIndex = pills.indexOf(current);
    // Written as the attribute, and compared as the attribute: a pill with no tabindex already READS as
    // -1, and only the attribute makes it focusable, so comparing the property would leave every pill
    // but the first out of reach of the arrow keys.
    for (const pill of pills) {
      const stop = pill === current ? '0' : '-1';
      if (pill.getAttribute('tabindex') !== stop) pill.setAttribute('tabindex', stop);
    }
  }

  private focusPill(pill: HTMLElement): void {
    this.activeIndex = this.pills().indexOf(pill);
    this.applyTabStops();
    pill.focus();
  }

  private isRtl(): boolean {
    const dir = this.closest('[dir]')?.getAttribute('dir') ?? document.documentElement.getAttribute('dir');
    return dir === 'rtl';
  }

  private onKeydown(e: KeyboardEvent): void {
    const visible = this.visiblePills();
    const pill = e.target as HTMLElement;
    const index = visible.indexOf(pill);
    if (index < 0) return;
    const move = (delta: number): void => {
      e.preventDefault();
      this.focusPill(visible[Math.max(0, Math.min(visible.length - 1, index + delta))]!);
    };
    switch (e.key) {
      case 'ArrowRight':
        move(this.isRtl() ? -1 : 1);
        break;
      case 'ArrowLeft':
        move(this.isRtl() ? 1 : -1);
        break;
      case 'ArrowDown':
        move(1);
        break;
      case 'ArrowUp':
        move(-1);
        break;
      case 'Home':
        move(-index);
        break;
      case 'End':
        move(visible.length - 1 - index);
        break;
      case 'Delete':
      case 'Backspace': {
        if (!pill.hasAttribute('removable')) return;
        e.preventDefault();
        this.removing = { pill, index };
        const target = pill as Removable;
        const unrefused =
          typeof target.requestRemove === 'function'
            ? target.requestRemove()
            : pill.dispatchEvent(new CustomEvent('caos-tag-remove', { bubbles: true, composed: true, cancelable: true }));
        if (!unrefused) this.removing = null;
        break;
      }
    }
  }
}

define('caos-pill-container', CaosPillContainer);

declare global {
  interface HTMLElementTagNameMap {
    'caos-pill-container': CaosPillContainer;
  }
}
